/* eslint-disable no-mixed-spaces-and-tabs */
import { CLAIM, INSIGHT_CLAIM } from '@/utils/emailHelper';
import {
	Box,
	Chip,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import React from 'react';

const BorderPreview = ({ style, width = '2px' }) => {
	const theme = useTheme();

	const getBorder = (s) => {
		if (s === 'hidden') return '1px hidden transparent';
		if (s === 'double') return `3px double ${theme.palette.text.primary}`;
		return `${width} ${s} ${theme.palette.text.primary}`;
	};

	return (
		<Box
			sx={{
				width: '100%',
				height: '8px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				pointerEvents: 'none',
			}}
		>
			<Box
				sx={{
					width: '100%',
					borderBottom: getBorder(style),
				}}
			/>
		</Box>
	);
};

const ToolbarDropdown = React.forwardRef(
	({ title, showSelection, defaultValue = '', fields, onChange, sx = {}, ...props }, ref) => {
		const theme = useTheme();
		const minimalInputTheme = theme.components.MuiInputBase.styleOverrides.minimal;

		const val = showSelection ? defaultValue : `Insert ${title} data`;
		const label = showSelection ? title : val;

		return (
			<Select
				ref={ref}
				variant='standard'
				className='minimal-input'
				size='small'
				value={val}
				displayEmpty
				onChange={onChange}
				renderValue={(selected) => {
					if (!showSelection) return val;

					const defaultField = fields.find((f) => f.value === f.default);
					const selectedField = fields.find((f) => f.value === selected) ?? defaultField;
					const isNotDefault =
						showSelection && selectedField?.value !== selectedField?.default;

					return (
						<Box
							component='span'
							className='active-element'
							sx={{
								width: '100%',
								fontWeight: isNotDefault ? 600 : 400,
								color: isNotDefault
									? theme.palette.primary.main
									: theme.palette.text.primary,
								fontSize: '12px',
								...(selectedField?.type === 'font' &&
									selectedField?.css && {
										fontFamily: selectedField.css,
									}),
								...(['borderStyle', 'borderWidth'].includes(
									selectedField?.type,
								) && {
									display: 'flex',
									alignItems: 'center',
									gap: 1,
								}),
							}}
						>
							{selectedField &&
							['borderStyle', 'borderWidth'].includes(selectedField?.type) ? (
								<>
									<span style={{ flexGrow: 1 }}>
										{selectedField?.label ?? defaultField?.label ?? 'Normal'}
									</span>
									<BorderPreview
										style={selectedField.style ?? selectedField.value}
										{...(selectedField?.type === 'borderWidth' && {
											width: selectedField?.value,
										})}
									/>
								</>
							) : (
								(selectedField?.label ?? defaultField?.label ?? 'Normal')
							)}
						</Box>
					);
				}}
				sx={{
					...minimalInputTheme,
					'& .MuiInput-input': {
						color: theme.palette.text.primary,
						fontWeight: 400,
						fontSize: '12px',
						paddingBottom: 0,
					},
					'& .MuiInput-input:hover': {
						color: theme.palette.primary.main,
					},
					paddingLeft: '8px',
					...sx,
				}}
				disableUnderline
				{...(props ?? {})}
			>
				{fields.every((f) => f.value !== defaultValue) && (
					<MenuItem
						value={val}
						disabled
						sx={{
							fontSize: '12px',
						}}
					>
						{val}
					</MenuItem>
				)}

				{fields.map((f) => {
					const value = f.label ?? 'None';

					return (
						<MenuItem
							key={`${f.type}-${f.value}`}
							value={f.value}
							disabled={f.disabled}
							sx={{
								...(f.type === 'font' &&
									f.css && {
										fontFamily: f.css,
									}),
								...(f.type !== 'header' && {
									fontSize: f.type === 'size' ? f.value : '12px',
								}),
								...(['borderStyle', 'borderWidth'].includes(f.type) && {
									display: 'flex',
									alignItems: 'center',
									gap: 1,
								}),
							}}
						>
							<Tooltip title={f.disabled ? f.disabledText : ''} placement='top'>
								{f.type === 'header' && typeof f.value === 'number' ? (
									React.createElement(
										`h${f.value}`,
										{
											style: {
												margin: 0,
											},
										},
										f.label,
									)
								) : ['borderStyle', 'borderWidth'].includes(f.type) ? (
									<>
										<span style={{ flexGrow: 1 }}>{value}</span>
										<BorderPreview
											style={f.style ?? f.value}
											{...(f.type === 'borderWidth' && {
												width: f.value,
											})}
										/>
									</>
								) : [CLAIM, INSIGHT_CLAIM].includes(f.type) &&
								  fields.some(
										(field) =>
											field.type !== CLAIM && field.type !== INSIGHT_CLAIM,
								  ) ? (
									<Stack
										direction='row'
										sx={{
											width: '100%',
											justifyContent: 'space-between',
											alignItems: 'center',
										}}
									>
										{value}
										<Chip
											size='small'
											variant='outlined'
											sx={{
												borderColor: theme.palette.folioWarningMid,
												color: theme.palette.folioWarningMid,
												ml: '1em',
											}}
											label={'claim'}
										/>
									</Stack>
								) : (
									<span>{value}</span>
								)}
							</Tooltip>
						</MenuItem>
					);
				})}
			</Select>
		);
	},
);
ToolbarDropdown.displayName = 'ToolbarDropdown';
export default ToolbarDropdown;
